import React from "react";
import { View, Text, Image, Pressable, Linking } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { getFormattedSupabaseUrl } from "shared-utils";

interface AttachmentProps {
    attachment: {
        id: number;
        fileUrl: string;
        fileName: string;
        fileType: string;
        fileSize: number;
    };
    isFromCurrentUser: boolean;
}

export default function AttachmentPreview({ attachment, isFromCurrentUser }: AttachmentProps) {
    const handleOpenAttachment = () => {
        // Get the proper URL using the shared helper function
        const fileUrl = getFormattedSupabaseUrl(
            attachment.fileUrl,
            process.env.EXPO_PUBLIC_SUPABASE_URL
        );

        // Open the URL
        Linking.canOpenURL(fileUrl).then(supported => {
            if (supported) {
                Linking.openURL(fileUrl);
            } else {
                console.error('Cannot open URL:', fileUrl);
                alert(`Cannot open this attachment (${attachment.fileName})`);
            }
        }).catch(err => {
            console.error('Error opening attachment:', err);
            alert('Failed to open the attachment. Please try again.');
        });
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        else return (bytes / 1048576).toFixed(1) + ' MB';
    };

    // Make sure the URL is properly formatted for Supabase storage
    const displayUrl = getFormattedSupabaseUrl(
        attachment.fileUrl,
        process.env.EXPO_PUBLIC_SUPABASE_URL
    );

    return (
        <Pressable
            className="flex-row items-center bg-white bg-opacity-20 rounded-md p-2"
            onPress={handleOpenAttachment}
        >
            {attachment.fileType.startsWith('image/') ? (
                <Image
                    source={{ uri: displayUrl }}
                    className="w-10 h-10 rounded-sm"
                    resizeMode="cover"
                />
            ) : (
                <View className="w-10 h-10 rounded-sm bg-white bg-opacity-30 items-center justify-center">
                    <Ionicons
                        name={
                            attachment.fileType.includes('pdf') ? 'document-text' :
                                attachment.fileType.includes('doc') ? 'document' :
                                    attachment.fileType.includes('sheet') ? 'grid' :
                                        'document-attach'
                        }
                        size={20}
                        color={isFromCurrentUser ? "white" : "#333"}
                    />
                </View>
            )}
            <View className="ml-2 flex-1">
                <Text
                    className={`text-xs font-medium ${isFromCurrentUser ? "text-white" : "text-gray-800"}`}
                    numberOfLines={1}
                >
                    {attachment.fileName}
                </Text>
                <Text
                    className={`text-xs ${isFromCurrentUser ? "text-white opacity-80" : "text-gray-600"}`}
                >
                    {formatFileSize(attachment.fileSize)}
                </Text>
            </View>
            <Ionicons
                name="open-outline"
                size={18}
                color={isFromCurrentUser ? "white" : "#666"}
            />
        </Pressable>
    );
} 